import { showToast } from '@/features/toasts/toastsSlice';
import { useWorkspace } from '@/features/workspaces/hooks';
import { DeleteModalWithConfirmInput } from '@/lib/Modals';
import { Audience } from '@/models/audiences';
import { DistributionChannel } from '@/models/distributionChannels';
import { ToastType } from '@/models/toast';
import { useAppDispatch } from '@/reducers';
import { useDisconnectDistributionChannelMutation } from '@/services/endpoints/distributionChannels';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

type DisconnectDestinationModalProps = {
  audience: Audience;
  destination: DistributionChannel;
  closeModal: VoidFunction;
};

export const DisconnectDestinationModal: FC<DisconnectDestinationModalProps> = ({ audience, destination, closeModal }) => {
  const workspace = useWorkspace();
  const dispatch = useAppDispatch();
  const { t } = useTranslation('audience_details');

  const [disconnectDistributionChannel, { isLoading, isSuccess }] = useDisconnectDistributionChannelMutation();

  const handleDeleteClicked = async () => {
    disconnectDistributionChannel({
      workspaceId: workspace.id,
      audienceId: audience.id,
      distributionChannelId: destination.id,
    });
  };

  useEffect(() => {
    if (isSuccess) {
      closeModal();

      dispatch(
        showToast({
          type: ToastType.SUCCESS,
          title: t('disconnect.success_title'),
          message: t('disconnect.success_body', { destinationName: destination.name }),
        })
      );
    }
  }, [isSuccess]);

  return (
    <DeleteModalWithConfirmInput
      inputComparisonValue={destination.name}
      confirmText='audience_details.disconnect.confirm_button'
      onConfirm={handleDeleteClicked}
      onClose={closeModal}
      isLoading={isLoading}
    >
      <DeleteModalWithConfirmInput.Title>{t('disconnect.confirm_title')}</DeleteModalWithConfirmInput.Title>

      <DeleteModalWithConfirmInput.InputHint>
        {t('disconnect.confirm_body')} &nbsp;(<b>{destination.name}</b>)
      </DeleteModalWithConfirmInput.InputHint>
    </DeleteModalWithConfirmInput>
  );
};
